import Dialog from '@mui/material/Dialog';
import { DialogTitle, DialogActions, TextField, Box, Button, FormHelperText, Grid, DialogContent } from '@mui/material';
import { CustomerContactsModel } from 'src/types/CustomerContactsModel';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import api from '../../api/Api';
import { Logger } from 'src/utils/logger';

interface ContactEditFormProps {
  onSubmit?: () => void;
  onCancel: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  contact: CustomerContactsModel;
  title: string;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const ContactEditForm: React.FC<React.PropsWithChildren<ContactEditFormProps>> = (props) => {
  const { customerNumber } = useParams();

  return (
    <Dialog maxWidth={props.maxWidth || 'xs'} fullWidth open={props.isOpen} onClose={props.onCancel}>
      <DialogTitle id="confirmation-dialog-title">{props.title} </DialogTitle>

      <DialogContent sx={{ paddingTop: '5px !important' }}>
        <Formik
          initialValues={props.contact}
          validationSchema={Yup.object().shape({
            firstName: Yup.string().max(255).required('name is required'),
            email: Yup.string().email('Must be a valid email').max(255),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }): Promise<void> => {
            delete values.submit;
            props.onSubmit();

            try {
              let success = await api.post<CustomerContactsModel>(`/customer/${customerNumber}/contact/update`, {
                ...props.contact,
                ...values,
                customerNumber: parseInt(customerNumber),
              });
              if (success) {
                setStatus({ success: true });
                setSubmitting(false);
                props.onSuccess();
                console.log(success, 'loading contacts');
              } else {
                Logger.error('Could not update user, check server log');
              }
            } catch (err) {
              console.log(err);
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label="First Name"
                    type="text"
                    name="firstName"
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                    variant="outlined"
                    // margin="normal"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField label="Last Name" type="text" name="lastName" onBlur={handleBlur} onChange={handleChange} value={values.lastName} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    autoFocus
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    label="Email Address"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="email"
                    value={values.email}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField autoFocus label="Job Title" name="title" onBlur={handleBlur} onChange={handleChange} type="text" value={values.title} variant="outlined" />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <TextField
                    autoFocus
                    label="Phone Number"
                    name="phoneNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="phone"
                    value={values.phoneNumber}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField autoFocus label="Fax" name="faxNumber" onBlur={handleBlur} onChange={handleChange} type="text" value={values.faxNumber} variant="outlined" />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    autoFocus
                    sx={{ width: '100%' }}
                    label="Address"
                    name="address1"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.address1}
                    variant="outlined"
                  />
                </Grid>

                <Grid size={{ xs: 5 }}>
                  <TextField autoFocus label="City" name="city" onBlur={handleBlur} onChange={handleChange} type="text" value={values.city} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <TextField autoFocus label="State" name="state" onBlur={handleBlur} onChange={handleChange} type="fax" value={values.state} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField autoFocus label="Zip" name="zipcode" onBlur={handleBlur} onChange={handleChange} type="fax" value={values.zipcode} variant="outlined" />
                </Grid>
              </Grid>

              {errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}

              <DialogActions>
                <Box sx={{ mt: 2 }}>
                  <Button color="primary" disabled={isSubmitting} type="submit" variant="contained" sx={{ m: 2 }}>
                    Submit
                  </Button>
                  <Button type={'submit'} variant="outlined" onClick={props.onCancel}>
                    Cancel
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
export default ContactEditForm;
